/* eslint-disable import/no-cycle */
import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { useCallback, useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { loadRoles, rolesSlice } from "./RolesState";
import { ApiState } from "./lib/ApiState";
import { executionsSlice, loadExecutions } from "./ExecutionsState";
import { loadScenarios, scenariosSlice } from "./ScenariosState";
import { loadWorkers, workersSlice } from "./WorkersState";
import { executionErrorsSlice, loadExecutionErrors } from "./ExecutionErrorsState";
/* eslint-enable import/no-cycle */

const apiReducers = {
    executionErrors: executionErrorsSlice.reducer,
    executions: executionsSlice.reducer,
    roles: rolesSlice.reducer,
    scenarios: scenariosSlice.reducer,
    workers: workersSlice.reducer
};
const rootReducer = combineReducers(apiReducers);

export const ClearStore = {
    type: "Root:Clear"
};

export const store = configureStore({
    middleware: getDefaultMiddleware => getDefaultMiddleware({
        immutableCheck: false,
        serializableCheck: false
    }),
    reducer: <RootState>(state: RootState, action: { type: string }) => {
        if (action === ClearStore) {
            return rootReducer(undefined, action);
        }

        // See https://github.com/rt2zz/redux-persist/issues/1140
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return rootReducer(state as any, action);
    }
});

export type ApiReducerKey = keyof typeof apiReducers;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const apiLoaders: { [key in ApiReducerKey]: (force?: boolean) => any } = {
    executionErrors: loadExecutionErrors,
    executions: loadExecutions,
    roles: loadRoles,
    scenarios: loadScenarios,
    workers: loadWorkers
};

function loadIfDefined<T extends ApiReducerKey>(dispatch: RootDispatch, key: T, force?: boolean) {
    const loader = apiLoaders[key];
    if (loader !== undefined) {
        dispatch(loader(force));
    }
}

export const reloadStateIfExists = (dispatch: RootDispatch, key: ApiReducerKey): void => {
    if (store.getState()[key]?.value) {
        loadIfDefined(dispatch, key, true);
    }
};

export const reloadAllState = (dispatch: RootDispatch): void => {
    for (const key of Object.keys(apiReducers) as ApiReducerKey[]) {
        if (store.getState()[key]?.value) {
            loadIfDefined(dispatch, key, true);
        }
    }
};

export type RootState = ReturnType<typeof rootReducer>;
export type RootDispatch = typeof store.dispatch;
export const useAppDispatch = useDispatch.withTypes<RootDispatch>();

export type ApiStateResponseError = {
    message: string;
    status: number;
    key: ApiReducerKey;
};

export type ApiStateResponse<T extends ApiReducerKey> = {
    [K in T]: RootState[K]
} & {
    meta: ApiStateMetadata;
};

export type ApiStateMetadata = {
    // Whether all states have been initialized, i.e. either error or value is set
    finished: boolean;
    // Whether any state is still loading
    loading: boolean;
    // Whether any state is still initially loading
    initialLoading: boolean;
    // The first error that occurred in any of the specified states
    error: ApiStateResponseError | undefined;
    // The retry for all errors
    retry: (() => void) | undefined;
};

type ApiStateRequestOptions = {
    autoLoad?: boolean;
    ignoreError?: boolean;
};

// For the purpose of this class, see method #makeSureThisIsAClass()
export class ApiStateRequest<T extends ApiReducerKey> {
    public readonly key: T;
    public readonly autoLoad?: boolean;
    public readonly ignoreError?: boolean;

    constructor(key: T, options: ApiStateRequestOptions) {
        this.key = key;
        this.autoLoad = options.autoLoad ?? true;
        this.ignoreError = options.ignoreError ?? false;
    }

    // This method serves the purpose to make sure that an instance of this class does not equal
    // on-the-fly objects such as { key: "test", autoLoad: false }

    // eslint-disable-next-line class-methods-use-this
    private makeSureThisIsAClass() {
    }
}

export function useApiStateRequest<T extends ApiReducerKey>(
    key: T,
    options: ApiStateRequestOptions
): ApiStateRequest<T> {
    const {
        autoLoad,
        ignoreError
    } = options;
    return useMemo(
        () => new ApiStateRequest(key, {
            autoLoad,
            ignoreError
        }),
        [key, autoLoad, ignoreError]
    );
}

export function useApiStates<T extends ApiReducerKey>(
    ...keys: (T | ApiStateRequest<T>)[]
): ApiStateResponse<T> {
    const dispatch = useAppDispatch();

    const load = useCallback((force?: boolean) => {
        for (const key of keys) {
            if (typeof key === "string" || key.autoLoad !== false) {
                const loader = typeof key === "string" ? apiLoaders[key] : apiLoaders[key.key];
                dispatch(loader(force));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, ...keys]);

    // Initial loading
    useEffect(() => {
        load();
    }, [load]);

    const data = useSelector((state: RootState) => {
        const dataObject: Partial<{ [key in ApiReducerKey]: ApiState }> = {};
        for (const key of keys) {
            const stringKey = typeof key === "string" ? key : key.key;
            dataObject[stringKey] = state[stringKey];
        }
        return dataObject;
        // ShallowEqual prevents useless re-renders
    }, shallowEqual);

    const loading = Object.values<ApiState>(data)
        .some(state => state.loading);
    const initialLoading = Object.values<ApiState>(data)
        .some(state => state.initialLoading);
    const finished = !Object.values<ApiState>(data)
        .some(state => state.value === undefined && state.error === undefined);

    const {
        error,
        retry
    }: {
        error: ApiStateResponseError | undefined,
        retry: (() => void) | undefined
    } = useMemo(() => {
        const stateErrors = keys.map(key => {
            const stringKey = typeof key === "string" ? key : key.key;
            const ignoreError = typeof key !== "string" && key.ignoreError;
            const state: ApiState = store.getState()[stringKey];

            return {
                message: ignoreError ? undefined : state.error,
                loader: apiLoaders[stringKey],
                status: state.statusCode,
                key: stringKey
            };
        })
            .filter(e => !!e.message);

        if (stateErrors.length === 0) {
            return {
                error: undefined,
                retry: undefined
            };
        }

        return {
            error: {
                message: stateErrors[0].message ?? "Unknown error",
                status: stateErrors[0].status ?? -1,
                key: stateErrors[0].key
            },
            retry: () => {
                stateErrors.forEach(({ loader }) => dispatch(loader(true)));
            }
        };
        // We need data here to make sure the error is up-to-date
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, data, ...keys]);

    const meta = useMemo(() => ({
        loading,
        initialLoading,
        error,
        retry,
        finished
    }), [loading, initialLoading, error, retry, finished]);

    return useMemo(() => {
        return {
            ...data,
            meta
        } as ApiStateResponse<T>;
    }, [data, meta]);
}
